import React, { useRef, useState, useContext } from 'react'
import { FileUpload } from 'primereact/fileupload'
import { ProgressBar } from 'primereact/progressbar'
import { Tag } from 'primereact/tag'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { MButton, LoadingContext } from 'src/M_Components'

export function convertToBase64(file) {
    return new Promise((resolve, reject) => readFileAsBase64(file, resolve, reject))
}

export function readFileAsBase64(file, resolve, reject) {
    const reader = new FileReader()
    reader.onload = () =>
        resolve({
            ...file,
            id: `${file.name}-${Date.now()}`,
            name: file.name,
            base64: reader.result,
            date: new Date().toLocaleDateString(),
        })
    reader.onerror = (error) => reject(new Error(error))
    reader.readAsDataURL(file)
}

export function formatBytes(bytes) {
    if (!bytes || bytes <= 0) return "0.00 B"
    const sizes = ["B", "KB", "MB"]
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`
}

export default function MUploadFiles({ maxSize = 10000000, multipleFiles, nameURL, onFilesChange, bodyImage }) {
    const [files, setFiles] = useState([])
    const [totalSize, setTotalSize] = useState(0)
    const fileUploadRef = useRef(null)
    const { t } = useTranslation()
    const ctx = useContext(LoadingContext)

    const progressBarColor = ctx?.primaryColor || 'var(--color-black)'

    const updateFiles = (newFiles) => {
        setFiles(newFiles)
        if (onFilesChange) {
            onFilesChange(newFiles)
        }
    }

    const onTemplateSelect = (e) => {
        let _totalSize = totalSize
        let newFiles = [...files]

        processFiles({ ...e, files: e?.originalEvent?.target?.files }, _totalSize, newFiles).catch((error) => {
            console.error('Error converting to base64:', error)
        })
    }

    async function processFiles(e, _totalSize, newFiles) {
        const duplicateNames = []

        const base64Files = await Promise.all(
            Object.keys(e.files).map(async (key) => {
                const file = e.files[key]
                if (newFiles.some((existingFile) => existingFile.name === file.name)) {
                    duplicateNames.push(file.name)
                    return null
                }

                if (file.size) {
                    _totalSize += file.size
                }
                const base64File = await convertToBase64(file)
                return base64File
            })
        )

        const validFiles = base64Files.filter((file) => file !== null)

        if (duplicateNames.length > 0) {
            ctx?.toastMessages?.('warning', '', `${t('DuplicateFile')} ${duplicateNames.join(', ')}`)
            return
        }

        if (_totalSize > maxSize) {
            setTotalSize((prev) => Math.min(prev, maxSize))
            ctx?.toastMessages?.('error', '', `${t('SizeFilesError')} ${formatBytes(maxSize)}`)
            return
        }        

        newFiles = [...newFiles, ...validFiles]
        setTotalSize(_totalSize)
        updateFiles(newFiles)
    }


    const onTemplateRemove = (file, callback) => {
        const newFiles = files.filter((f) => f.name !== file.name)
        setTotalSize((prevTotalSize) => Math.max(0, prevTotalSize - (file.size || 0)))
        updateFiles(newFiles)
        callback()
    }

    const onTemplateClear = () => {
        setTotalSize(0)
        updateFiles([])
        if (fileUploadRef.current) {
            fileUploadRef.current.clear()
        }
    }
    

    const headerTemplate = (options) => {
        const { className, chooseButton, cancelButton } = options
        const value = (totalSize / maxSize) * 100

        return (
            <div className={className} style={{ backgroundColor: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div data-testid="choose-button">{chooseButton}</div>
                    <div data-testid="clear-button">{cancelButton}</div>
                </div>
                <div>
                    <span data-testid="file-size">{formatBytes(totalSize)} / {formatBytes(maxSize)}</span>
                    <ProgressBar
                        value={value}
                        color={progressBarColor}
                        showValue={false}
                        style={{ width: '10rem', height: '12px' }}
                        data-testid="progress-bar"
                    />
                </div>
            </div>
        )
    }

    const itemTemplate = (file, value) => {
        const fileExtension = file.name.toLowerCase()
        const getIcon = () => {
            switch (true) {
                case fileExtension.endsWith('.pdf'):
                    return "/UploadFilesImages/pdf.png"
                case fileExtension.endsWith('.txt'):
                    return "/UploadFilesImages/txt.png"
                case fileExtension.endsWith('.xlsx') || fileExtension.endsWith('.csv'):
                    return "/UploadFilesImages/excel.png"
                case fileExtension.endsWith('.pptx'):
                    return "/UploadFilesImages/powerpoint.png"
                case fileExtension.endsWith('.docx') || fileExtension.endsWith('.odt'):
                    return "/UploadFilesImages/office.png"
                case fileExtension.endsWith('.rar') || fileExtension.endsWith('.zip'):
                    return "/UploadFilesImages/zip.png"
                default:
                    return file.objectURL
            }
        }

        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '40%', display: 'flex', alignItems: 'center' }}>
                    <div>
                        <img
                            src={getIcon()}
                            alt={file.name}
                            role="presentation"
                            width={40}
                            style={{ marginRight: '10px' }}
                            data-testid="custom-icon"
                        />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                        <div data-testid="file-name">
                            {file.name}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'left', marginTop: '5px' }}>
                            <small style={{ marginLeft: '10px' }}>{new Date().toLocaleDateString()}</small>
                            <Tag value={value.formatSize} className="mr-2" icon="pi pi-info-circle" severity="warning" style={{ marginLeft: '20px' }} rounded />
                        </div>
                    </div>
                </div>
                <div style={{ width: '40%' }} data-testid="custom-body">
                    {bodyImage ? bodyImage(file) : null}
                </div>
                <div style={{ width: '20%', display: 'flex', justifyContent: 'right', alignItems: 'center' }}>
                    <MButton
                        data-testid="delete-button"
                        icon={<img
                            src="./icons/svgsDaNet/trash-2.svg"
                            alt="Delete Icon"
                            style={{ filter: 'invert(16%) sepia(66%) saturate(5073%) hue-rotate(347deg) brightness(74%) contrast(116%)', width: '25px', cursor: 'pointer' }}
                        />}
                        onClick={() => onTemplateRemove(file, value.onRemove)}
                        label={"Remove"}
                    />
                </div>
            </div>
        )
    }

    const emptyTemplate = () => (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <div>
                <i className="pi pi-image" style={{ fontSize: '3em', borderRadius: '50%', color: 'var(--surface-d)', width: '100%', display: 'flex', justifyContent: 'center' }}></i>
                <div style={{ fontSize: '1em', color: 'var(--color-grey)' }}>
                    {t("DragAnDrop")}
                </div>
            </div>
        </div>
    )

    const chooseOptions = { icon: 'pi pi-fw pi-images', iconOnly: true, style: { backgroundColor: 'var(--color-blue)', width: '20px', height: '20px', padding: '20px', borderRadius: '50%', border: '1px solid var(--color-blue)' } }
    const cancelOptions = { icon: 'pi pi-fw pi-times', iconOnly: true, style: { backgroundColor: 'var(--color-red-dark)', width: '20px', height: '20px', padding: '20px', borderRadius: '50%', border: '1px solid var(--color-red-dark)' } }

    return (
        <div>
            <FileUpload
                ref={fileUploadRef}
                name={nameURL}
                multiple={multipleFiles || false}
                maxFileSize={maxSize}
                onSelect={onTemplateSelect}
                onError={onTemplateClear}
                onClear={onTemplateClear}
                headerTemplate={headerTemplate}
                itemTemplate={itemTemplate}
                emptyTemplate={emptyTemplate}
                chooseOptions={chooseOptions}
                cancelOptions={cancelOptions}
                data-testid="file-upload-input"
            />
        </div>
    )
}

MUploadFiles.propTypes = {
    maxSize: PropTypes.number,
    multipleFiles: PropTypes.bool,
    nameURL: PropTypes.string,
    onFilesChange: PropTypes.func,
    bodyImage: PropTypes.func,
}
